
import Enumerable from "linq";
import { Component, Mixins } from "vue-property-decorator";
import { IDocumentImageSource, ImageKinds, ImageSourceKinds } from "../../../../../domain/document";
import {
  IImageDrawable, instanceOfIImageDrawable,
} from "../../../../../domain/document/items";
import { CommonComponentsAccessor } from "../../CommonComponentsAccessor";

@Component
export default class ClaioApplicationLaunchableMixin extends Mixins() {
  editingSource: IImageDrawable;
  writePropertyWhenValid: (writer: (source: IImageDrawable) => void) => void;
  commonComponents: CommonComponentsAccessor;

  async launchClaioApplicationWhenNeeded(): Promise<boolean> {
    return await this.editClaioSchemaWhenNeeded(true)
        || this.showClaioContentWhenNeeded(this.editingSource);
  }

  showClaioContentWhenNeeded(source: IImageDrawable): boolean {
    const adapter = this.commonComponents.adapters.claio;
    if (!adapter.isContentShowable(source)) {
      return false;
    }
    adapter.showContent(source);
    return true;
  }

  async editClaioSchemaWhenNeeded(isClaioSchemaOnly: boolean): Promise<boolean> {
    const adapter = this.commonComponents.adapters.claioSchema;
    if (!adapter.isContentEditable(this.editingSource, isClaioSchemaOnly)) {
      return false;
    }
    const result = await adapter.editContent(this.editingSource);
    if (result && result.isComplete) {
      this.writePropertyWhenValid(s => {
        this.fillOriginalImageSource(s);
        s.imageSource = result.imageSource;
        s.imageKind = this.getEditedImageKind(s.imageKind);
        if (result.drawLayerImageSource) {
          s.drawLayerImageSource = result.drawLayerImageSource;
        } else {
          s.drawLayerImageSource.imageGuid = null;
          s.drawLayerImageSource.imageSourceKind = ImageSourceKinds.None;
        }
        s.drawObjectData = result.drawObjectData;
      });
    }
    return true;
  }

  /**
   * V1で通常画像を登録したとき、その時点では原本画像は無く、編集時に原本画像として複製している模様
   */
  private fillOriginalImageSource(item: IImageDrawable): void {
    if (this.isBlankImageSource(item.originalImageSource) && !this.isBlankImageSource(item.imageSource)) {
      item.originalImageSource = item.imageSource.clone() as IDocumentImageSource;
    }
  }

  private isBlankImageSource(imageSource: IDocumentImageSource): boolean {
    return (imageSource == null || imageSource.imageSourceKind == null || imageSource.imageSourceKind === ImageSourceKinds.None);
  }

  private getEditedImageKind(imageKind: ImageKinds): ImageKinds {
    if (imageKind == null) { return ImageKinds.Image; }
    return (imageKind === ImageKinds.ClaioSchema) 
      ? ImageKinds.ClaioSchemaEdit
      : imageKind;
  }

  async editClaioWhenNeeded(): Promise<boolean> {
    const adapter = this.commonComponents.adapters.proRadDraw;
    if (!adapter.isContentEditable(this.editingSource)) {
      return false;
    }
    const result = await adapter.editContents(new Array(this.editingSource));
    if (result && result.isComplete && 0 < result.images.length) {
      this.writePropertyWhenValid(s => {
        this.fillOriginalImageSource(s);
        s.imageSource = result.images[0].imageSource;
        // TODO imageKindはClaioImageのままでいいのか検討
      });
    }
    return true;
  }

  async editClaioImagesWhenNeeded(): Promise<boolean> {
    const adapter = this.commonComponents.adapters.proRadDraw;
    if (!adapter.isContentEditable(this.editingSource)) {
      return false;
    }
    const editDocument = this.commonComponents.editors.extraPage.editingSource;
    const imageItems = Enumerable.from(editDocument.items)
        .where(i => instanceOfIImageDrawable(i) && adapter.isContentEditable(i) && this.editingSource.privateId !== i.privateId)
        .select(i => i as IImageDrawable);
    // 右クリックしたものを編集起動時に表示するため1番目に持ってくる
    const editImageItems = Enumerable.from(new Array(this.editingSource)).concat(imageItems).toArray();
    const result = await adapter.editContents(editImageItems);
    if (result && result.isComplete && 0 < result.images.length) {
      Enumerable.from(result.images).forEach((image, index) => {
        if (!!image && !!image.imageSource) {
          const updateImage = editImageItems[index].clone() as IImageDrawable;
          this.fillOriginalImageSource(updateImage);
          updateImage.imageSource = image.imageSource;
          editDocument.updateItem(updateImage);
        }
      });
    }
    return true;
  }

  get isEditableClaioImage(): boolean {
    const adapter = this.commonComponents.adapters.proRadDraw;
    return adapter.isContentEditable(this.editingSource);
  }

  get isClaioSchemaEditableImage(): boolean {
    const adapter = this.commonComponents.adapters.claioSchema;
    const isClaioSchemaOnly = !this.$configuration.behavior.document.editor.imageEditor.claioSchema.isEnable;
    return adapter.isContentEditable(this.editingSource, isClaioSchemaOnly);
  }
}
